/**
 * Owner-configurable brand + product configuration (spec §21).
 * Nothing here is a promise about what is built — see `docs/feature-status.md`
 * for what actually works. Values that are not yet real are marked `null`
 * so the UI can hide the control rather than fake it.
 */

export const BRAND = {
  /** Split so the wordmark can style the bracketed (x). */
  nameParts: ['e', 'x', 'amine'] as const,
  spokenName: 'examine',
  tagline: 'Read a story. Make it yours.',
  supportEmail: null as string | null,
  /** Set only when a real App Store listing exists (spec §5 W01 item 1). */
  appStoreUrl: null as string | null,
  publisher: null as string | null,
} as const;

/**
 * Feature availability flags. These gate whether a control is *shown*,
 * so the product never displays an action that cannot work (spec §7 C06-C07:
 * "Do not show unavailable controls as if they work").
 */
export const CAPABILITIES = {
  /** Public Magic Gallery requires a live backend + moderation staffing. */
  publicGallery: false,
  /** Sparkles require the gallery. */
  sparkles: false,
  /** Website checkout requires configured Stripe products. */
  webBilling: false,
  /** StoreKit requires the owner's App Store Connect products. */
  nativeBilling: false,
  /**
   * Cloud sync — real when a sync service is configured at build time, and
   * absent otherwise. It is not a promise of a service; it is a statement
   * about whether one is wired up.
   */
  cloudSync: Boolean(import.meta.env.VITE_SYNC_URL),
  /** Local-first storage — this one is real today. */
  localLibrary: true,
  /**
   * Keepsake PDF building. Real in the app; the small shared preview build
   * leaves the PDF engine out, and says so rather than offering a button that
   * cannot work.
   */
  pdfExport: import.meta.env.VITE_NO_PDF !== '1',
  /**
   * Saving a file to the device. Real in the app and in a normal browser. The
   * shared web preview runs inside a sandbox that silently discards every
   * download, so there it is false and the UI says so — a button that appears
   * to save and does not is exactly the failure §7 C06-C07 forbids.
   */
  fileSave: import.meta.env.VITE_SANDBOXED !== '1',
} as const;

export type CapabilityKey = keyof typeof CAPABILITIES;

/**
 * The sync service this build talks to (spec §21).
 *
 * Set `VITE_SYNC_URL` to the deployed service. With it unset there is no
 * sign-in screen, no sync switch, and no "coming soon" — the product is simply
 * the local-only app it then is.
 */
export const SYNC = {
  baseUrl: (import.meta.env.VITE_SYNC_URL as string | undefined)?.replace(/\/$/, '') ?? '',
} as const;
